import React, { useEffect, useState, useRef, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import FormBuilderComponent from './form_builder';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import SwitchInput from '../../common/Form/switchInput';
import TextInput from '../../common/Form/textInput';
import Grid from 'apollo-react/components/Grid';
import SelectInput from '../../common/Form/selectInput';
import CommonModal from '../../common/CommonModal';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';
import { createSubformSchema } from '../../../validation/subform';

const CreateForms = ({ persona }) => {
  const [t] = useTranslation();
  const [form, setForm] = useState({
    form_id: 0,
    name: '',
    description: '',
    template: false,
  });
  const [subFormsTemplate, setSubFormsTemplate] = useState([]);
  const [discardModal, setDiscardModal] = useState(false);
  const [builderDirty, setBuilderDirty] = useState(false);
  const formBuilderSaveRef = useRef(null);

  const isDirty = useMemo(() => {
    return (
      form.name.trim() !== '' ||
      form.description.trim() !== '' ||
      form.template !== false ||
      builderDirty
    );
  }, [form.name, form.description, form.template, builderDirty]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  const getSubFormsTemplate = () => {
    axiosInstance
      .get('/api/subforms-template')
      .then((resp) => {
        // console.log(resp);

        setSubFormsTemplate(resp.data);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    getSubFormsTemplate();
    //console.log(forms);
  }, []);

  const [formDataFields, setFormDataFields] = useState({
    isTemplate: false,
    formFields: [],
  });

  const handleFormSave = async (formJsonData) => {
    // The validation here is manual (not passing the schema to formik) since
    // we don't submit through formik but formBuilder component.
    const validationResult = createSubformSchema(t).safeParse({
      name: form.name,
      description: form.description,
      template: form.template,
      jsonObject: formJsonData,
    });
    if (!validationResult.success) {
      const messages = validationResult.error.issues.map((d) => d.message);
      toast.error(messages.join('\n'), { position: 'top-center' });
      return false;
    }
    try {
      const updatedForm = { ...form };

      const url = form.template ? 'api/subforms-template' : 'api/subforms';
      // eslint-disable-next-line no-undef
      const fullUrl = `${process.env.REACT_APP_SERVER_URL}/${url}`;

      await axiosInstance.post(
        fullUrl,
        {
          ...updatedForm,
          require_approval: 0,
          json_object: formJsonData,
          approved: 0,
          version: 0.1,
        },
        { withCredentials: true }
      );

      toast.success(t('Form Created Successfully!'), {
        position: 'top-center',
      });
      navigateBack();
    } catch (error) {
      console.error(t('Error saving form'), ':', error);
      const details = error?.response?.data?.error?.details;
      if (details) {
        const messages = Array.isArray(details)
          ? details.map((d) => d.message || 'Validation error')
          : Object.values(details);
        toast.error(messages.join('\n'), { position: 'top-center' });
      } else {
        const message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          t('Failed to save form.');
        toast.error(message, { position: 'top-center' });
      }
      return false;
    }
  };

  const handleFormChange = (name, value) => {
    if (name === 'sub_form_id') {
      const selectedForm = subFormsTemplate.find(
        (form) => form.id === Number(value)
      );
      setForm({
        ...selectedForm,
        [name]: value,
        name: form.name,
        form_id: '',
        description: '',
        template: form.template,
      });

      if (selectedForm && selectedForm['json_object'])
        setFormDataFields({
          formFields: JSON.parse(selectedForm['json_object']),
          isTemplate: true,
        });
      else setFormDataFields({ ...formDataFields, formFields: [] });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  const renderBasicFormInfo = () => {
    const basicFormInfo = (
      <div>
        <Field name="name">
          {({ field, meta }) => (
            <TextInput
              {...field}
              meta={meta}
              label={'Subform name'}
              value={form?.[`name`] || ''}
              onChange={(value) => handleFormChange('name', value)}
              required
              // fullWidth={false}
            />
          )}
        </Field>

        <Field name="description">
          {({ field, meta }) => (
            <TextInput
              {...field}
              meta={meta}
              label={'Subform Description'}
              value={form?.[`description`]}
              onChange={(value) => handleFormChange('description', value)}
              multiline={true}
              textareaWidth={'100%'}
            />
          )}
        </Field>
      </div>
    );
    return basicFormInfo;
  };

  return (
    <div
      className="content"
      style={{
        border: 'solid 1px #E9E9E9',
        padding: '24px',
        borderRadius: '5px',
      }}
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <Formik
            initialValues={form}
            enableReinitialize={true}
            onSubmit={() => {}}
          >
            <Form>
              {renderBasicFormInfo()}

              <Field name="sub_form_id">
                {({ field, meta }) => (
                  <SelectInput
                    {...field}
                    meta={meta}
                    label={'Clone predefined Subforms'}
                    options={subFormsTemplate?.map((form) => ({
                      key: form.id,
                      value: form.id,
                      label: `${form.name}`,
                    }))}
                    onChange={(e, value) => {
                      handleFormChange('sub_form_id', e);
                    }}
                    value={form.sub_form_id}
                  />
                )}
              </Field>

              <Field name="template">
                {({ field, meta }) => (
                  <SwitchInput
                    {...field}
                    meta={meta}
                    label={t('Save as Template')}
                    checked={form.template === 1}
                    value={form?.template}
                    onChange={(checked) => {
                      handleFormChange('template', checked);
                    }}
                    style={{
                      marginTop: '16px',
                      marginBottom: '8px',
                    }}
                  />
                )}
              </Field>
            </Form>
          </Formik>
        </Grid>
      </Grid>
      <FormBuilderComponent
        formData={formDataFields.formFields}
        onFormSave={handleFormSave}
        isTemplate={formDataFields.isTemplate}
        handleDiscardModal={() => setDiscardModal(!discardModal)}
        saveRef={formBuilderSaveRef}
        onSchemaChange={(dirty) => setBuilderDirty(dirty)}
      />

      <CommonModal
        open={discardModal}
        message="Are you sure you want to discard your changes?"
        buttonProps={[
          {
            onClick: () => {
              setDiscardModal(false);
            },
          },
          {
            label: 'Confirm',
            onClick: () => {
              setForm({
                form_id: 0,
                name: '',
                description: '',
                template: false,
              });
              setDiscardModal(false);
              setBuilderDirty(false);
              setFormDataFields({
                ...formDataFields,
                formFields: [],
                isTemplate: true,
              });
            },
          },
        ]}
        hideHeader
        id="hideHeader"
      />
    </div>
  );
};

export default CreateForms;
